import {type RouteLocationNormalizedLoaded, useRoute} from "vue-router";
import {inject, InjectionKey, provide} from "vue";
import {buildRouteNameFromPath} from "@/router/Router.ts";

export interface VirtualRouteAccess {
	getParam: (name:string) => string|undefined;
	getParamNames: () => string[];
	getPath: () => string;
}

export interface VRoute {
	/** Route path, e.g. "/editParty.do" */
	readonly path: string;
	/** Name of the route, e.g. "editParty" */
	readonly name: string;
	/** Query parameters */
	readonly query: Readonly<Record<string, string|undefined>>;
}

export function getAttParam(route: RouteLocationNormalizedLoaded, name: string): string|null|undefined {
	const qval = route.query[name];
	const val = Array.isArray(qval) ? qval[0] : qval;
	return val == null ? undefined : val;
}

/** @deprecated */
export function getIntAttParam(route: RouteLocationNormalizedLoaded, name: string): number {
	const strVal = getAttParam(route, name);
	if (strVal == null) return 0;
	return parseInt(strVal);
}

const virtualRouteKey = Symbol() as InjectionKey<VRoute>;

export function provideVirtualRoute(access:VirtualRouteAccess):void {
	const contextPath = window.Server.context;
	const queryProxy:Readonly<Record<string, string | undefined>> = new Proxy<Readonly<Record<string, string | undefined>>>({}, {
		get(_, p: string, receiver: any): string {
			if (typeof p !== 'string') return undefined;
			return access.getParam(p);
		},

		ownKeys(_): ArrayLike<string> {
			return access.getParamNames();
		},

		getOwnPropertyDescriptor(_, p) {
			if (typeof p !== "string") return undefined;
			if (typeof access.getParam(p) === "undefined") return undefined;
			return {
				configurable: true,
				enumerable: true
			};
		},

		has(_, p: string): boolean {
			return typeof access.getParam(p) !== 'undefined'
		}
	});
	const vr:VRoute = {
		get path():string {
			return contextPath + access.getPath();
		},
		get name():string {
			return buildRouteNameFromPath(access.getPath());
		},
		query:queryProxy
	};

	provide(virtualRouteKey, vr);
}


export function useVirtualRoute(): VRoute {
	const vr = inject(virtualRouteKey, null);
	if (vr == null) {
		const global = useRoute();

		const queryProxy:Readonly<Record<string, string | undefined>> = new Proxy<Readonly<Record<string, string | undefined>>>({}, {
			get(_, p: string, receiver: any): string {
				if (typeof p !== 'string') return undefined;
				return getAttParam(global, p);
			},

			getOwnPropertyDescriptor(_, p) {
				if (typeof p !== "string") return undefined;
				if (typeof global.query[p] === "undefined") return undefined;
				return {
					configurable: true,
					enumerable: true
				};
			},

			ownKeys(_): ArrayLike<string> {
				return Object.keys(global.query);
			},

			has(_, p: string): boolean {
				return typeof p === "string" && Object.prototype.hasOwnProperty.call(global.query, p);
			}
		});

		return {
			get path():string {
				return global.path;
			},
			get name():string {
				return global.name == null ? "" : global.name.toString();
			},
			query:queryProxy

		}
	} else {
		return vr;
	}
}